'use client'

import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import type { ComponentProps, CSSProperties, PropsWithChildren } from 'react'

import { cn } from '@/src/utils/styles'

const getTransformation = (
  side: 'top' | 'right' | 'bottom' | 'left',
): CSSProperties => {
  switch (side) {
    case 'top':
    case 'bottom':
      return {
        transform: 'scaleY(-1)',
      }
    case 'left':
    case 'right':
      return {
        transform: 'scaleX(-1)',
        rotate: '180deg',
      }
  }
}

const TooltipProvider = TooltipPrimitive.Provider

const Tooltip = TooltipPrimitive.Root

const TooltipTrigger = TooltipPrimitive.Trigger

interface TooltipContentProps
  extends ComponentProps<typeof TooltipPrimitive.Content> {
  hideArrow?: boolean
}

const tooltipClassNames =
  'rounded-xl text-primary outline outline-1 outline-border1 bg-surface1-100 p-3 shadow-md'

const TooltipContainer = ({ children }: PropsWithChildren) => {
  return <div className={tooltipClassNames}>{children}</div>
}

const TooltipContent = ({
  className,
  sideOffset = 6,
  side,
  hideArrow = true,
  ref,
  ...props
}: TooltipContentProps) => {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        side={side}
        className={cn('max-w-78', tooltipClassNames, className)}
        {...props}
      >
        {!hideArrow && (
          <TooltipPrimitive.Arrow asChild>
            <svg
              width='17'
              height='7'
              viewBox='0 0 17 7'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              style={getTransformation(side ?? 'top')}
            >
              <path
                d='M8.5 14.1426L1.42893 7.07151L6.37868 2.12176C7.55025 0.950191 9.44975 0.950191 10.6213 2.12176L15.5711 7.07151L8.5 14.1426Z'
                fill='#231E1D'
              />
              <path
                d='M8.14645 14.4961L8.5 14.8497L8.85355 14.4961L15.9246 7.42506L16.2782 7.07151L15.9246 6.71796L10.9749 1.76821C9.60804 0.401375 7.39196 0.401375 6.02513 1.76821L1.07538 6.71796L0.721825 7.07151L1.07538 7.42506L8.14645 14.4961Z'
                stroke='white'
                strokeOpacity='0.16'
              />
            </svg>
          </TooltipPrimitive.Arrow>
        )}
        {props.children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

TooltipContent.displayName = TooltipPrimitive.Content.displayName

const TooltipTitle = ({ className, ref, ...props }: ComponentProps<'h6'>) => (
  <h6
    ref={ref}
    className={cn('text-xs text-primary font-semibold', className)}
    {...props}
  />
)

TooltipTitle.displayName = 'TooltipTitle'

const TooltipDescription = ({
  className,
  ref,
  ...props
}: ComponentProps<'p'>) => (
  <p
    ref={ref}
    className={cn('text-xs text-secondary font-[450] max-w-64', className)}
    {...props}
  />
)

TooltipDescription.displayName = 'TooltipDescription'

export {
  Tooltip,
  TooltipContainer,
  TooltipContent,
  TooltipDescription,
  TooltipProvider,
  TooltipTitle,
  TooltipTrigger,
}
